import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Send, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMAIL_KEY = "nc_chronicles_email";

function readSavedEmail() {
  try {
    return localStorage.getItem(EMAIL_KEY) || "";
  } catch {
    return "";
  }
}

function saveEmail(em) {
  try {
    localStorage.setItem(EMAIL_KEY, em);
  } catch {
    /* ignore */
  }
}

export default function ChronicleInteractions({ slug }) {
  const [email, setEmail] = useState(readSavedEmail);
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  // comment form
  const [cName, setCName] = useState("");
  const [cEmail, setCEmail] = useState(readSavedEmail);
  const [cBody, setCBody] = useState("");
  const [cBusy, setCBusy] = useState(false);
  const [cDone, setCDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const em = readSavedEmail();
      const { data: d } = await api.get(
        `/public/chronicles/${slug}/interactions${em ? `?email=${encodeURIComponent(em)}` : ""}`
      );
      setData(d);
    } catch {
      /* non-fatal */
    }
  }, [slug]);

  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, [load]);

  const requireEmail = () => {
    const em = email.trim();
    if (!em) {
      toast.error("Add your email below to join in.");
      return null;
    }
    saveEmail(em);
    if (!cEmail) setCEmail(em);
    return em;
  };

  const react = async (key) => {
    const em = requireEmail();
    if (!em) return;
    setBusyKey(key);
    try {
      const { data: d } = await api.post(`/public/chronicles/${slug}/react`, { email: em, reaction_key: key });
      setData((prev) => ({ ...prev, reactions: d.reactions, my_reactions: d.my_reactions }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't save your reaction.");
    } finally {
      setBusyKey(null);
    }
  };

  const vote = async (idx) => {
    const em = requireEmail();
    if (!em) return;
    setBusyKey(`poll-${idx}`);
    try {
      const { data: d } = await api.post(`/public/chronicles/${slug}/poll-vote`, { email: em, option_index: idx });
      setData((prev) => ({ ...prev, poll: d.poll, my_vote: d.my_vote }));
      toast.success("Vote counted. 👑");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't record your vote.");
    } finally {
      setBusyKey(null);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!cName.trim() || !cEmail.trim() || !cBody.trim()) {
      toast.error("Name, email and a message are all required.");
      return;
    }
    setCBusy(true);
    try {
      await api.post(`/public/chronicles/${slug}/comment`, {
        name: cName.trim(),
        email: cEmail.trim(),
        body: cBody.trim(),
      });
      saveEmail(cEmail.trim());
      setCDone(true);
      setCBody("");
      toast.success("Thank you — your comment is awaiting Nina's approval.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't post your comment.");
    } finally {
      setCBusy(false);
    }
  };

  if (!data) {
    return (
      <section className="mt-20 pt-12 border-t border-white/10" data-testid="chronicle-interactions-loading">
        <div className="h-3 w-28 mx-auto bg-white/5 mb-4" />
        <div className="h-8 w-64 mx-auto bg-white/5 mb-10" />
        <div className="flex justify-center gap-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 w-16 bg-white/5" />
          ))}
        </div>
      </section>
    );
  }

  const labels = data.reaction_labels || {};
  const counts = data.reactions || {};
  const mine = data.my_reactions || [];
  const poll = data.poll;
  const comments = data.comments || [];

  return (
    <section className="mt-20 pt-12 border-t border-white/10" data-testid="chronicle-interactions">
      <p className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--gold)] text-center mb-2">
        After The Read
      </p>
      <h3 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-center mb-3">
        Tell me what you felt.
      </h3>
      <p className="text-white/55 text-sm text-center max-w-md mx-auto mb-10">
        React, vote on what happens next, or leave a note. I read every one.
      </p>

      {/* Shared email field for reactions + poll */}
      <div className="max-w-md mx-auto mb-10">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => email.trim() && saveEmail(email.trim())}
          placeholder="your@email.com — to react & vote"
          className="w-full bg-transparent border border-white/15 rounded-none px-4 h-11 text-sm outline-none focus:border-[color:var(--gold)] transition-colors text-center"
          data-testid="chronicle-interaction-email"
        />
      </div>

      {/* Reactions */}
      <div className="flex flex-wrap justify-center gap-3 mb-14" data-testid="chronicle-reactions">
        {Object.entries(labels).map(([key, label]) => {
          const active = mine.includes(key);
          const emoji = label.split(" ")[0];
          const text = label.slice(emoji.length).trim();
          return (
            <button
              key={key}
              onClick={() => react(key)}
              disabled={busyKey === key}
              className={`group inline-flex items-center gap-2 px-4 h-12 border rounded-none text-sm transition-all duration-200 disabled:opacity-50 ${
                active
                  ? "border-[color:var(--gold)] bg-[color:var(--gold)]/10 text-[color:var(--gold)]"
                  : "border-white/15 text-white/70 hover:border-[color:var(--gold)]/50"
              }`}
              data-testid={`chronicle-react-${key}`}
            >
              <span className="text-lg leading-none">{emoji}</span>
              <span className="hidden sm:inline">{text}</span>
              <span className="tabular-nums text-xs text-white/50" data-testid={`chronicle-react-count-${key}`}>
                {counts[key] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Poll */}
      {poll && (
        <div className="max-w-xl mx-auto mb-16" data-testid="chronicle-poll">
          <div className="flex items-center gap-2 justify-center mb-5">
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">Reader Poll</span>
          </div>
          <h4 className="font-serif text-xl sm:text-2xl text-center mb-6">{poll.question}</h4>
          <div className="space-y-3">
            {poll.options.map((opt, idx) => {
              const total = poll.total || 0;
              const tally = poll.tallies[idx] || 0;
              const pct = total ? Math.round((tally / total) * 100) : 0;
              const voted = data.my_vote === idx;
              return (
                <button
                  key={idx}
                  onClick={() => vote(idx)}
                  disabled={busyKey === `poll-${idx}`}
                  className={`relative w-full text-left overflow-hidden border rounded-none px-4 py-3 transition-colors disabled:opacity-60 ${
                    voted ? "border-[color:var(--gold)]" : "border-white/15 hover:border-[color:var(--gold)]/40"
                  }`}
                  data-testid={`chronicle-poll-option-${idx}`}
                >
                  <span
                    className="absolute inset-y-0 left-0 bg-[color:var(--gold)]/12 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                  <span className="relative flex items-center justify-between gap-3">
                    <span className="text-sm flex items-center gap-2">
                      {voted && <Check size={14} style={{ color: "var(--gold)" }} />}
                      {opt}
                    </span>
                    <span className="text-xs tabular-nums text-white/55">{pct}%</span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-white/35 text-center mt-3">
            {poll.total} vote{poll.total === 1 ? "" : "s"} · one per reader
          </p>
        </div>
      )}

      {/* Comments */}
      <div className="max-w-2xl mx-auto" data-testid="chronicle-comments">
        <div className="flex items-center gap-2 mb-6">
          <MessageCircle size={16} style={{ color: "var(--gold)" }} />
          <h4 className="font-serif text-xl">
            Comments {comments.length > 0 && <span className="text-white/40 text-base">({comments.length})</span>}
          </h4>
        </div>

        {/* Comment form */}
        {cDone ? (
          <div
            className="surface p-6 text-center text-sm text-[color:var(--gold)] inline-flex items-center justify-center gap-2 w-full mb-10"
            data-testid="chronicle-comment-done"
          >
            <Check size={16} /> Your comment is in Nina&rsquo;s queue. It appears once approved.
          </div>
        ) : (
          <form onSubmit={submitComment} className="surface p-6 mb-10 space-y-3" data-testid="chronicle-comment-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="text"
                value={cName}
                onChange={(e) => setCName(e.target.value)}
                placeholder="Your name"
                className="bg-transparent border border-white/15 rounded-none px-4 h-11 text-sm outline-none focus:border-[color:var(--gold)] transition-colors"
                data-testid="chronicle-comment-name"
              />
              <input
                type="email"
                value={cEmail}
                onChange={(e) => setCEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-transparent border border-white/15 rounded-none px-4 h-11 text-sm outline-none focus:border-[color:var(--gold)] transition-colors"
                data-testid="chronicle-comment-email"
              />
            </div>
            <textarea
              value={cBody}
              onChange={(e) => setCBody(e.target.value)}
              placeholder="What did this chapter stir up in you?"
              rows={3}
              className="w-full bg-transparent border border-white/15 rounded-none px-4 py-3 text-sm outline-none focus:border-[color:var(--gold)] transition-colors resize-none"
              data-testid="chronicle-comment-body"
            />
            <button
              type="submit"
              disabled={cBusy}
              className="btn-gold rounded-none px-6 h-11 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="chronicle-comment-submit"
            >
              <Send size={13} />
              {cBusy ? "Sending…" : "Post Comment"}
            </button>
            <p className="text-[11px] text-white/35">Comments are reviewed before they appear.</p>
          </form>
        )}

        {/* Approved comments */}
        {comments.length === 0 ? (
          <p className="text-white/40 text-sm text-center py-4">Be the first to leave a comment.</p>
        ) : (
          <ul className="space-y-5">
            {comments.map((c) => (
              <li key={c.id} className="surface p-5" data-testid={`chronicle-comment-${c.id}`}>
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <span className="font-serif text-[color:var(--gold)]">{c.name}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/30">
                    {(c.created_at || "").slice(0, 10)}
                  </span>
                </div>
                <p className="text-sm text-white/75 leading-relaxed whitespace-pre-line">{c.body}</p>
                {c.admin_reply && (
                  <div className="mt-4 pl-4 border-l-2 border-[color:var(--gold)]/50">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--gold)] mb-1">
                      Nina replied
                    </p>
                    <p className="text-sm text-white/70 leading-relaxed whitespace-pre-line">{c.admin_reply}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
